'use client'

import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { Fragment, useEffect, useState } from 'react'

import ButtonCartGroup from '@/components/common/ButtonCartGroup'
import ProductTags from '@/components/common/ProductTags'
import Currency from '@/components/ui/Currency'
import Icon from '@/components/ui/icon'
import ShowUnit from '@/components/ui/ShowUnit'
import { cn, loadImage, numberUnit } from '@/lib/utils'
import { ProductFullSearchData } from '@/types/product.type'

import Tooltips from '@/components/ui/Tooltips'
import ZoomImage from './ZoomImage'

type ProductCardListProps = {
  product: ProductFullSearchData
}

export default function ProductCardList({ product }: ProductCardListProps) {
  const t = useTranslations('common')
  const [isDisabled, setIsDisabled] = useState(false)
  const tOrder = useTranslations('my_order')

  const checkProductTags = (tags: ProductFullSearchData['product_tags'], tagIds: number[]) => {
    let hasBannedTag = false
    for (const tagId of tagIds) {
      if (tags?.some(tag => tag.product_tag_id === tagId)) {
        hasBannedTag = true
        break
      }
    }
    return hasBannedTag
  }

  useEffect(() => {
    if (!product) return

    if (
      // product.product_tag_id === 18 || product.product_tag_id === 15 || 
      checkProductTags(product.product_tags, [18, 15])) {
      setIsDisabled(true)
    } else {
      setIsDisabled(false)
    }
  }, [product])

  return (
    <div className="flex">
      <ZoomImage
        href={`/${product.slug}`}
        src={loadImage('medium', product.image_id)}
        alt={product.name}
        imageClassName="!h-[150px] !w-[150px] max-w-[150px] !rounded-none"
        imageScale={2}
      >
        {product?.product_tags?.length > 0 && <ProductTags tags={product.product_tags} />}
        {product.price_change && (
          <span className="tag-discount !text-[11px]">
            {t('discount')} {Math.abs(product.price_change)}%
          </span>
        )}
      </ZoomImage>
      <div className="flex pl-[15px] w-full">
        <div className="flex-auto pr-[15px]">
          <div className="relative inline-flex items-center group cursor-pointer mt-2">
            <Link href={`/${product.slug}`} target="_blank" className="text-ellipsis break-words line-clamp-3 text-sm font-normal leading-[21px] hover:text-primary">
              {product.name}
            </Link>
            <Tooltips className='z-[99] font-normal' placement="bottom" style="warning" message={`${product?.name}`} />
          </div>
          {/* <div className="relative flex items-center group cursor-pointer mt-2 w-fit">
            <Link href={`/${product?.slug}`} target="_blank" className="text-sm hover:underline text-primary">
              {`IC${product.id}`}
            </Link>
            <Tooltips className='z-[99] font-normal' placement="right" style="warning" message={`IC${product?.id} Thegioiic part#`} />
          </div> */}
          <div className="mt-2 text-sm text-gray-800">{product.supplier.name}</div>
          <div className="mt-2 text-sm">
            <Currency className="font-bold" currency={product.price} lang="vi" />/<ShowUnit unit={product.unit} />
          </div>
          <div className='flex gap-6 justify-start items-center mt-2'>
            {product?.sheetfile && (
              <div className="relative inline-flex items-center group cursor-pointer mt-2">
                <Link href={product?.sheetfile ?? '#'} target="_blank" aria-label={`${product?.sheetname} | Data Sheet`}>
                  <Icon icon="tgic icon-file-pdf" className="text-red-500 text-3xl" />
                </Link>
                <Tooltips className="z-[99] font-normal" placement="right" style="warning" message={`${product?.sheetname} | Data Sheet`} />
              </div>
            )}
            <div>{product.rohs && <Icon icon="tgic icon-rohs" className="text-green-500 text-4xl" />}</div>
          </div>
        </div>
        <div className="flex flex-col justify-between">
          <div className="leading-5 text-[15px]">
            <div
              className={cn('w-[250px] flex items-center p-1', {
                'bg-aqua text-[#008000]': product.inventory > 0,
                'bg-warning !text-red-600': product.inventory <= 0,
              })}
            >
              <Icon icon={product.inventory > 0 ? 'tgic icon-check' : 'tgic icon-info-solid'} style={{ WebkitTextStroke: product.inventory > 0 ? '1px #008000' : '' }} />
              <div className="flex justify-between items-center w-full">
                <div className="grid ml-2">
                  {product.inventory > 0 && (
                    <Fragment>
                      <span>
                        <b>
                          {t('in_stock')}: {numberUnit(product.inventory)} <ShowUnit unit={product.unit} />
                        </b>
                      </span>
                      <span>{t('instock_message')}</span>
                    </Fragment>
                  )}
                  {product.inventory <= 0 && (
                    <Fragment>
                      <span>{t('out_of_stock')}</span>
                      {!isDisabled && <span>{t('outstock_message')}</span>}
                    </Fragment>
                  )}
                </div>
              
              </div>
            </div>
            <ButtonCartGroup
              prices={product.prices}
              className="mt-2 pt-1"
              buttonSize="xs"
              multiples={product.multiples}
              productId={product.id}
              product={product}
              minQuantity={product.minimum_order_quantity}
              isDisabled={isDisabled}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
